import { useState } from 'react';

import { useSelector } from 'react-redux';

import { getAndSaveConsumerMetadata } from '@atc/consumer-metadata';
import { useFeatures } from '@atc/features';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import PriceDropAlertsFetcher from '@/fetchers/vdp/PriceDropAlertsFetcher';

/**
 * Custom hook for handling vehicle price alert subscription functionality.
 *
 * This hook manages the complete workflow for subscribing to price alerts on vehicles,
 * including form submission, API calls, consumer metadata updates, and error/success
 * state management. It integrates with Redux for vehicle data, feature flags for
 * branding, and external services for price alerts and consumer tracking.
 *
 * @hook
 * @example
 * // Basic usage in a component
 * function PriceAlertComponent() {
 *   const {
 *     onPriceAlertSubmit,
 *     showPriceAlertError,
 *     showPriceAlertSuccess
 *   } = usePriceAlertSubmit();
 *
 *   const handleSubmit = (formData) => {
 *     onPriceAlertSubmit({ emailAddress: formData.email });
 *   };
 *
 *   return (
 *     <div>
 *       {showPriceAlertError && <div>Error occurred</div>}
 *       {showPriceAlertSuccess && <div>Success!</div>}
 *       <button onClick={handleSubmit}>Subscribe</button>
 *     </div>
 *   );
 * }
 *
 * @returns {Object} Hook return object
 * @returns {Function} returns.onPriceAlertSubmit - Async function to handle form submission.
 *   Expects an object with emailAddress property. Validates listing data, calls
 *   PriceDropAlertsFetcher, updates consumer metadata, and manages success/error states.
 * @returns {boolean} returns.showPriceAlertError - Boolean indicating if an error occurred
 *   during submission. Set to true when validation fails, API returns non-200 response,
 *   or an exception is thrown.
 * @returns {boolean} returns.showPriceAlertSuccess - Boolean indicating successful
 *   subscription. Set to true when API returns 200 response and all operations complete.
 *
 * @since 1.0.0
 *
 * @requires useSelector - Redux hook for accessing vehicle listing data
 * @requires useFeatures - Feature flag hook for brand configuration
 * @requires useState - React hook for managing error/success states
 *
 * @see {@link PriceDropAlertsFetcher} - API service for price alert subscription
 * @see {@link getAndSaveConsumerMetadata} - Service for updating consumer data
 * @see {@link vdpResultsDuck} - Redux duck for vehicle listing data
 *
 * @throws {Error} Logs errors to console when submission fails, sets showPriceAlertError to true
 *
 * @description
 * **Workflow:**
 * 1. Validates required listing data (id, owner.id, title)
 * 2. Calls PriceDropAlertsFetcher with form data and listing info
 * 3. Updates consumer metadata with email and brand info
 * 4. Sets appropriate success/error state based on response
 *
 * **Dependencies:**
 * - Active vehicle listing in Redux store
 * - Brand feature flag configuration
 * - Valid form data with emailAddress property
 */
export function usePriceAlertSubmit() {
    const {
        brand: [, { consumer_brand: consumerBrand }],
    } = useFeatures(['brand']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { id, owner, title } = listing;

    const [showPriceAlertError, setShowPriceAlertError] = useState(false);
    const [showPriceAlertSuccess, setShowPriceAlertSuccess] = useState(false);

    const onPriceAlertSubmit = async (data) => {
        // Reset states
        setShowPriceAlertError(false);
        setShowPriceAlertSuccess(false);

        const hasValidData = !!(id && owner?.id && title);

        try {
            const response = hasValidData && (await PriceDropAlertsFetcher({
                ...data,
                listingId: id,
                ownerId: owner.id,
                bookmarkTitle: title,
            }));

            await getAndSaveConsumerMetadata({
                email: data.emailAddress,
                brand: consumerBrand,
                shouldUpdateConsumerMetadata: true,
            });

            if (!hasValidData || response?.responseCode !== '200') {
                setShowPriceAlertError(true);
            } else {
                setShowPriceAlertSuccess(true);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Price alert submission failed:', error);
            setShowPriceAlertError(true);
        }
    };

    return {
        onPriceAlertSubmit,
        showPriceAlertError,
        showPriceAlertSuccess,
    };
}
